import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import Handsontable from "handsontable";
import { GridSettings } from "handsontable/settings";
import { ToastrService } from "ngx-toastr";
import { filter, map, pairwise, Subject, switchMap } from "rxjs";

import { environment } from "../../../../core/environments/environment";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { Language, PageMode } from "../../../constants/enums";
import { ActivityTargetModel, PeriodModelType } from "../../../models";
import { LanguageService, PeriodApiService } from "../../../services";

@Component({
  selector: "adaa-targets-tab",
  imports: [TranslatePipe],
  template: `
    @if (isTabActive()) {
      @if (hasErrors()) {
        @if (hasMissingDetails().fieldsMissing) {
          <div class="alert alert-danger my-1 border-0 rounded-1 fw-bold" role="alert">
            <i class="fa-solid fa-triangle-exclamation me-2" role="img" aria-label="Warning:"></i>
            {{ "nkpi.no_formula_info" | translate }}
          </div>
        }

        @if (hasMissingDetails().dates) {
          <div class="alert alert-danger my-1 border-0 rounded-1 fw-bold d-flex align-items-center" role="alert">
            <i class="fa-solid fa-triangle-exclamation mx-2" role="img" aria-label="Warning:"></i>
            {{ "notification.error.dates_difference" | translate }}
          </div>
        }
        @if (hasMissingDetails().periods) {
          <div class="alert alert-danger my-1 border-0 rounded-1 fw-bold d-flex align-items-center" role="alert">
            <i class="fa-solid fa-triangle-exclamation mx-2" role="img" aria-label="Warning:"></i>
            {{ "notification.error.no_periods" | translate }}
          </div>
        }
      } @else {
        <section class="hot-table" #hotTable></section>
      }
    }
  `,
})
export class TargetsTabComponent implements OnInit {
  private readonly _injector = inject(Injector);
  private readonly _toastrService = inject(ToastrService);
  private readonly _languageService = inject(LanguageService);
  private readonly _periodApiService = inject(PeriodApiService);

  isTabActive = input.required<boolean>();
  stagedActivity = input.required<Record<string, unknown>>();
  pageMode = input.required<PageMode>();

  hotTable = viewChild<ElementRef>("hotTable");

  private readonly _untilDestroy = AdaaHelper.untilDestroyed();
  private readonly _onChangeEvent = new Subject<{ eType: string; eData: unknown }>();
  private readonly _onChange = this._onChangeEvent.asObservable().pipe(pairwise());

  hotInstance: Handsontable | undefined;
  hasChanges = signal<boolean>(false);
  periods = signal<PeriodModelType[]>([]);
  activityTargets = signal<ActivityTargetModel[]>([]);
  targetErrors = signal<ActivityTargetModel[]>([]);

  stagedTargets = computed(() => this.stagedActivity()?.targets as ActivityTargetModel[]);

  dateRange = computed(
    () => ({
      start: this.stagedActivity()?.startDate as number | null,
      end: this.stagedActivity()?.endDate as number | null,
    }),
    {
      equal: (a, b) => a.start === b.start && a.end === b.end,
    }
  );

  hasMissingDetails = computed(() => {
    const errors: Record<string, true> = {};

    const { start, end } = this.dateRange();
    if (!start || !end) {
      errors.fieldsMissing = true;
    }
    if (this._isStartGreaterThanEnd(start, end)) {
      errors.dates = true;
    }
    if (!this.periods()?.length) {
      errors.periods = true;
    }

    return errors;
  });

  hasErrors = computed(() => Object.keys(this.hasMissingDetails()).length > 0);

  constructor() {
    this._hansontableEffect();
    this._periodChangeEffect();
  }

  public ngOnInit() {
    this._onValueChange();
  }

  private readonly _hansontableEffect = () => {
    effect(
      () => {
        if (this.isTabActive() && !this.hotInstance) {
          this._initHandsontable();
        } else {
          this.hotInstance = undefined;
        }
      },
      { injector: this._injector }
    );
  };

  readonly _periodChangeEffect = () => {
    effect(
      () => {
        const { start, end } = this.dateRange();
        const errors = this.hasMissingDetails();

        untracked(() => {
          if (errors.dates || errors.fieldsMissing) return;

          if (!this.periods().length) {
            this._fetchPeriodRange({
              frequency: Constants.FREQUENCY.ANNUAL,
              startTS: start as number,
              endTS: end as number,
            }).subscribe({
              next: (data) => this.periods.set(data ?? []),
              complete: () => {
                this._initTargetsList();
              },
            });
          }

          this._onChangeEvent.next({
            eType: "period-change",
            eData: {
              frequency: Constants.FREQUENCY.ANNUAL,
              startTS: start as number,
              endTS: end as number,
            },
          });
        });
      },
      { injector: this._injector }
    );
  };

  private readonly _refreshPeriodList = () => {
    return switchMap(([_, { eData }]) => {
      const data = eData as { startTS: number; endTS: number; frequency: number };

      return this._fetchPeriodRange(data);
    });
  };

  private readonly _hasDataChanged = () => {
    return filter(([old$, new$]) => {
      const oldData = old$.eData as { startTS: number; endTS: number; frequency: number };
      const newData = new$.eData as { startTS: number; endTS: number; frequency: number };

      if (oldData.frequency !== newData.frequency) return true;
      if (oldData.startTS !== newData.startTS) return true;
      return oldData.endTS !== newData.endTS;
    });
  };

  private _onValueChange() {
    this._onChange
      .pipe(
        filter(([_, $event]) => $event.eType === "period-change"),
        this._hasDataChanged(),
        this._refreshPeriodList(),
        this._untilDestroy()
      )
      .subscribe({
        next: (data) => {
          this.periods.set(data ?? []);
          this._initTargetsList();
        },
      });
  }

  private _fetchPeriodRange(data: { startTS: number; endTS: number; frequency: number }) {
    return this._periodApiService.getDateRange(data).pipe(map((res) => res.responseData));
  }

  private _initTargetsList() {
    const targets = this.stagedTargets() ?? [];
    const periods = this.periods();
    const count = periods.length;

    if (count === 0) {
      this.activityTargets.set([]);
      return;
    }

    const total = 1000; // Working with integers to avoid floating point issues (100.0 * 10)
    const base = Math.floor(total / count);
    const remainder = total % count;

    // Distribute remainder to the last items
    const distributedValues = Array.from({ length: count }, (_, i) => {
      // If we are in the last 'remainder' items, add 1 to the base
      // The last 'remainder' items indices are from (count - remainder) to (count - 1)
      const isInRemainderGroup = i >= count - remainder;
      return (isInRemainderGroup ? base + 1 : base) / 10;
    });

    this.activityTargets.set(
      periods.map<ActivityTargetModel>((e, index) => {
        const data = targets.find(({ periodId }) => e.id === periodId);
        return {
          target: data?.target ?? distributedValues[index],
          periodId: e.id,
          year: e.year,
        };
      })
    );
  }

  private _initHandsontable() {
    const container = this.hotTable()?.nativeElement as HTMLElement;
    const options = this._getOptions();

    if (container) {
      this.hotInstance = new Handsontable(container, options);
    }
  }

  private _getOptions(): GridSettings {
    const currentLang = this._languageService.current();
    const data = this._generateTableData();
    const { columnWidths, columns, columnLabels } = this._generateColumns();

    return {
      data,
      rowHeaders: this.activityTargets().map((i) => i.year.toString()),
      rowHeaderWidth: 300,
      colHeaders: columnLabels,
      columns: columns,
      colWidths: columnWidths,
      stretchH: "all",
      height: window.innerHeight * 0.6,
      viewportColumnRenderingOffset: 999999,
      viewportRowRenderingOffset: 999999,
      hiddenColumns: true,
      layoutDirection: this._languageService.direction(),
      language: currentLang === Language.Arabic ? "ar-AR" : "en-US",
      licenseKey: environment.handsontable_key,
      themeName: "ht-theme-classic",
      afterChange: (changes, _source) => this._onCellChange(changes),
      afterGetColHeader(col, TH) {
        if (TH) {
          TH.id = `col-header-${col}`;
        }
      },
    };
  }

  private _generateColumns() {
    return {
      columns: [
        { readonly: false }, //value
      ],
      columnWidths: [400],
      columnLabels: [this._languageService.translate("nkpi.targets")],
    };
  }

  private _generateTableData() {
    return this.activityTargets().map((i) => {
      return [i.target];
    });
  }

  private _isStartGreaterThanEnd(start: number | null, end: number | null) {
    if (!start || !end) return false;
    return start > end;
  }

  private _onCellChange(c: Handsontable.CellChange[] | null) {
    if (!AdaaHelper.isDefined(c)) return;

    this.hasChanges.set(true);

    for (const _c of c) {
      const [period, target, prev, curr] = _c;

      this._validateAndHighlight(+target, +period, curr);

      this.activityTargets.update((t) => {
        t[period].wasChanged = AdaaHelper.tableValuesEvaluator(prev) !== AdaaHelper.tableValuesEvaluator(curr);
        t[period].target = AdaaHelper.tableValuesEvaluator(curr);

        return t;
      });
    }
  }

  private _validateAndHighlight(col: number, row: number, value: unknown): void {
    const isInvalid = typeof value === "string" && !/^\d+(\.\d+)?$/.test(value.trim());

    const targetRow = row;
    const targetCol = col;

    if (isInvalid) {
      this.hotInstance?.setCellMeta(targetRow, targetCol, "valid", false);
      this.hotInstance?.setCellMeta(targetRow, targetCol, "className", "has-error");
    } else {
      this.hotInstance?.setCellMeta(targetRow, targetCol, "valid", true);
      this.hotInstance?.removeCellMeta(targetRow, targetCol, "className");
    }

    this.hotInstance?.render();
  }

  public submitTargets(reportError = true) {
    const activity = this.stagedActivity();

    // NOTE: on create error detection
    if (this.pageMode() === PageMode.create) {
      const errors = this.activityTargets().filter(({ target }) => {
        return !AdaaHelper.isDefined(target);
      });

      if (errors.length > 0) {
        this.targetErrors.set(errors);
        throw new Error("Missing Targets");
      } else this.targetErrors.set([]);

      let total = 0;
      this.activityTargets().forEach((e) => (total = total + (e.target ?? 0)));
      if (total !== 100) {
        this._toastrService.error(this._languageService.translate("activities.total_targets_error"));
        throw new Error("Targets Not Equal 100");
      }

      return {
        targets: this.activityTargets(),
        targetWasChanged: true,
      };
    }

    const errors = this.activityTargets().filter(({ target }) => {
      return !AdaaHelper.isDefined(target);
    });

    if (errors.length > 0 && reportError) {
      this.targetErrors.set(errors);
      throw new Error("Missing Targets");
    } else this.targetErrors.set([]);

    let total = 0;
    this.activityTargets().forEach((e) => (total = total + (e.target ?? 0)));
    if (total !== 100) {
      this._toastrService.error(this._languageService.translate("activities.total_targets_error"));
      throw new Error("Targets Not Equal 100");
    }

    let hasAnyChanges = false;

    this.activityTargets().forEach((p) => {
      const t = ((activity?.targets ?? []) as Record<string, unknown>[]).find(
        ({ periodId }) => periodId === p.periodId
      );
      if (!t) {
        hasAnyChanges = true;
        return;
      }

      hasAnyChanges = t.target !== p.target;
    });

    return {
      targetWasChanged: hasAnyChanges,
      targets: this.activityTargets().map((t) => {
        return t;
      }),
    };
  }

  public showCellsWithError() {
    const errors = this.targetErrors();
    const targets = this.activityTargets();

    this.hotInstance?.batch(() => {
      errors.forEach(({ periodId }) => {
        const i = targets.findIndex((e) => periodId === e.periodId);
        if (i < 0) return;

        const place: [row: number, col: number] = [i, 0];
        this.hotInstance?.setCellMeta(...place, "valid", false);
        this.hotInstance?.setCellMeta(...place, "className", "has-error");
      });
    });

    this.hotInstance?.render();
  }
}
